import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native'
import type { SharedValue } from 'react-native-reanimated'
import { useCallback, useEffect, useState } from 'react'
import { useWindowDimensions, View } from 'react-native'
import Animated, {
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { scheduleOnRN } from 'react-native-worklets'
import { useRouter } from 'expo-router'
import Head from 'expo-router/head'
import { useAppSession } from '@/src/contexts/AppSessionContext'
import { fetchDashboardWeeks } from '@/src/dal/contentful-dashboard-dal'
import { useRequiredAppSessionUser } from '@/src/hooks/useRequiredAppSessionUser'
import {
  resolveContentWeekFromActiveWeek,
  resolveDashboardAccessibleWeekCount,
  resolveDashboardWeeks,
  resolveProgramWeekCount,
} from '@/src/queries/dashboard-queries'
import { exerciseGoalQueryOptions } from '@/src/queries/exercise-queries'
import { queryKeys } from '@/src/queries/query-keys'
import {
  queryOptions,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { PullToRefreshScrollView } from '../../../components/ui/PullToRefreshScrollView'
import { APP_TOP_BAR_ROW_HEIGHT } from '../../../constants/app-top-bar-layout'
import { useContentfulLocale } from '../../../hooks/useContentfulLocale'
import { colors } from '../../../theme'
import { DashboardExerciseCard } from '../../../types/dashboard'
import { showAlert } from '../../../utils/alert'
import { getCurrentYearWeek } from '../../../utils/year-week'
import {
  ARC_HEIGHT,
  DAILY_TIP_HEADER_COLLAPSED_HEIGHT,
  DAILY_TIP_HEADER_EXPANDED_HEIGHT,
} from '../components/dashboard-layout-constants'
import {
  DailyWarmupCardSection,
  MeasurementReminderSection,
} from '../components/DashboardExercisesSection'
import { DashboardHeader } from '../components/DashboardHeader'
import { DashboardArcTransition } from '../components/DashboardScrollContent'
import { DashboardTopBar } from '../components/DashboardTopBar'
import { DashboardVoiceAssessmentSummary } from '../components/DashboardVoiceAssessmentSummary'
import { ViewedWeekWeeklyExercisesSection } from '../components/ViewedWeekWeeklyExercisesSection'
import { WeeklyStreakWidget } from '../components/WeeklyStreakWidget'
import { WeekPicker } from '../components/WeekPicker'
import { useDashboardTipVisibility } from '../hooks/useDashboardTipVisibility'
import {
  buildSessionParams,
  buildSessionRouteParams,
  shouldOpenExercisePreview,
} from '../utils/dashboard-screen-helpers'

function openDashboardExercise({
  effectiveWeek,
  exercise,
  router,
}: {
  effectiveWeek: number
  exercise: DashboardExerciseCard
  router: ReturnType<typeof useRouter>
}) {
  const sessionParams = buildSessionParams(exercise)
  const shouldOpenPreview = shouldOpenExercisePreview(exercise, effectiveWeek)

  if (shouldOpenPreview) {
    router.push({
      pathname: '/preview/[id]',
      params: sessionParams,
    })
    return
  }

  const sessionRouteParams = buildSessionRouteParams(sessionParams)
  router.push({
    pathname: '/session/[id]',
    params: sessionRouteParams,
  })
}

export default function Dashboard() {
  const { backendUser } = useRequiredAppSessionUser()

  const { hasPurchased } = useAppSession()
  const queryClient = useQueryClient()
  const router = useRouter()

  const insets = useSafeAreaInsets()

  const { t } = useTranslation(['dashboard', 'common'])
  const language = useContentfulLocale()

  const [isPullToRefreshing, setIsPullToRefreshing] = useState(false)
  const [isTopBarOnLightBackground, setIsTopBarOnLightBackground] =
    useState(false)

  const currentYearWeek = getCurrentYearWeek()

  const { data: weeks, refetch: refetchWeeks } = useSuspenseQuery(
    queryOptions({
      queryKey: queryKeys.dashboard.weeks.byLocale(language),
      queryFn: () => fetchDashboardWeeks(language),
    }),
  )

  const programWeekCount = resolveProgramWeekCount(weeks)
  const currentContentWeek = resolveContentWeekFromActiveWeek(
    backendUser.activeWeek,
    programWeekCount,
  )
  const resolvedWeek = resolveDashboardWeeks({
    currentProgramWeek: currentContentWeek,
    selectedWeek: currentContentWeek,
    weeks,
  })

  const maxSelectableWeek = resolveDashboardAccessibleWeekCount({
    activeWeek: backendUser.activeWeek,
    programWeekCount: resolvedWeek.programWeekCount,
  })
  const [selectedProgramWeekState, setSelectedProgramWeek] = useState(
    resolvedWeek.currentProgramWeek,
  )
  const selectedProgramWeek = Math.min(
    selectedProgramWeekState,
    maxSelectableWeek,
  )

  const { showTip } = useDashboardTipVisibility()
  const dashboardHeaderHeight =
    (showTip
      ? DAILY_TIP_HEADER_EXPANDED_HEIGHT
      : DAILY_TIP_HEADER_COLLAPSED_HEIGHT) + insets.top
  const refreshIndicatorOffset = dashboardHeaderHeight + 20

  const scrollOffsetY = useSharedValue(0)

  const handleExercisePress = (exercise: DashboardExerciseCard) => {
    if (!hasPurchased) {
      showAlert(t('common:error'), t('dashboard:subscription_required'))
      return
    }
    openDashboardExercise({
      effectiveWeek: selectedProgramWeek,
      exercise,
      router,
    })
  }

  const handleWeekSelect = (week: number) => {
    setSelectedProgramWeek(week)
  }

  const handleDashboardScroll = (
    event: NativeSyntheticEvent<NativeScrollEvent>,
  ) => {
    scrollOffsetY.value = event.nativeEvent.contentOffset.y
  }

  const handleRefresh = useCallback(async () => {
    setIsPullToRefreshing(true)

    await Promise.all([
      refetchWeeks(),
      queryClient.refetchQueries({
        queryKey: queryKeys.dashboard.tips.byLocale(language),
        exact: true,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.dashboard.morningRoutine.byLocale(language),
        exact: true,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.progress.snapshot(resolvedWeek.yearWeek),
        exact: true,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.app.all(language),
        exact: true,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.warmup.weekdayCompletion(currentYearWeek),
        exact: true,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.dashboard.subjects.byLocale(language),
        exact: true,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.dashboard.weeks.byLocale(language),
        exact: true,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.exercise.week(currentYearWeek),
        exact: true,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.activities.stats(),
        exact: true,
      }),
      queryClient.refetchQueries({
        queryKey: exerciseGoalQueryOptions().queryKey,
        exact: true,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.backendSessions.all(),
        exact: true,
      }),
    ]).finally(() => {
      setIsPullToRefreshing(false)
    })
  }, [
    currentYearWeek,
    language,
    queryClient,
    refetchWeeks,
    resolvedWeek.yearWeek,
  ])

  const contentSurfaceColor = colors.backgroundPrimary

  return (
    <View className="flex-1" style={{ backgroundColor: contentSurfaceColor }}>
      <Head>
        <title>{t('common:page_title_dashboard')}</title>
        <meta name="theme-color" content={contentSurfaceColor} />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content={contentSurfaceColor}
        />
      </Head>
      <DashboardTopBar isOnLightBackground={isTopBarOnLightBackground} />
      <AnimatedDashboardHeaderView
        scrollOffsetY={scrollOffsetY}
        setIsTopBarOnLightBackground={setIsTopBarOnLightBackground}
      />
      <PullToRefreshScrollView
        className="flex-1"
        refreshing={isPullToRefreshing}
        onRefresh={handleRefresh}
        indicatorColor={colors.primary}
        refreshControlProps={{
          colors: [colors.primary],
          tintColor: colors.primary,
          progressViewOffset: refreshIndicatorOffset,
        }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          flexGrow: 1,
          paddingTop: dashboardHeaderHeight,
        }}
        onScroll={handleDashboardScroll}
        scrollEventThrottle={16}
        renderContent={({ indicator, children }) => (
          <View
            className="flex-1 pt-3 pb-5 bg-background-primary px-4"
            style={{ marginTop: -1 }}
          >
            {indicator}
            {children}
          </View>
        )}
      >
        <MeasurementReminderSection />
        <DailyWarmupCardSection handleExercisePress={handleExercisePress} />
        <WeeklyStreakWidget />
        <DashboardVoiceAssessmentSummary />
        <ViewedWeekWeeklyExercisesSection
          currentProgramWeek={resolvedWeek.currentProgramWeek}
          handleExercisePress={handleExercisePress}
          hasPurchased={hasPurchased}
          language={language}
          selectedProgramWeek={Math.min(
            selectedProgramWeekState,
            maxSelectableWeek,
          )}
          weekPickerSlot={
            weeks == null ? undefined : (
              <WeekPicker
                viewedWeek={Math.min(
                  selectedProgramWeekState,
                  maxSelectableWeek,
                )}
                currentWeek={resolvedWeek.currentProgramWeek}
                maxWeek={maxSelectableWeek}
                onWeekChange={handleWeekSelect}
              />
            )
          }
        />
      </PullToRefreshScrollView>
    </View>
  )
}

function AnimatedDashboardHeaderView({
  scrollOffsetY,
  setIsTopBarOnLightBackground,
}: {
  scrollOffsetY: SharedValue<number>
  setIsTopBarOnLightBackground: (isLight: boolean) => void
}) {
  const { showTip } = useDashboardTipVisibility()
  const insets = useSafeAreaInsets()

  const { width: windowWidth } = useWindowDimensions()
  const arcRadius =
    windowWidth > 0
      ? (windowWidth * windowWidth) / (8 * ARC_HEIGHT) + ARC_HEIGHT / 2
      : 600
  const arcDiameter = arcRadius * 2
  const arcLeft = (windowWidth - arcDiameter) / 2
  const dashboardHeaderHeight =
    (showTip
      ? DAILY_TIP_HEADER_EXPANDED_HEIGHT
      : DAILY_TIP_HEADER_COLLAPSED_HEIGHT) + insets.top
  const topBarContrastSwitchOffset = Math.max(
    0,
    (showTip
      ? DAILY_TIP_HEADER_EXPANDED_HEIGHT
      : DAILY_TIP_HEADER_COLLAPSED_HEIGHT) -
      (showTip ? ARC_HEIGHT : 0) -
      APP_TOP_BAR_ROW_HEIGHT,
  )
  const topBarContrastSwitchOffsetShared = useSharedValue(
    topBarContrastSwitchOffset,
  )
  useEffect(() => {
    topBarContrastSwitchOffsetShared.value = topBarContrastSwitchOffset
  }, [topBarContrastSwitchOffset, topBarContrastSwitchOffsetShared])

  useAnimatedReaction(
    () =>
      Math.max(0, scrollOffsetY.value) > topBarContrastSwitchOffsetShared.value,
    (isLight, wasLight) => {
      if (isLight !== wasLight) {
        scheduleOnRN(setIsTopBarOnLightBackground, isLight)
      }
    },
  )

  const overlayAnimatedStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateY: -Math.min(
          Math.max(0, scrollOffsetY.value),
          dashboardHeaderHeight,
        ),
      },
    ],
  }))

  return (
    <Animated.View
      className="absolute left-0 right-0 top-0 z-10"
      style={[{ pointerEvents: 'box-none' }, overlayAnimatedStyle]}
    >
      <DashboardHeader topInset={insets.top} />
      <DashboardArcTransition
        arcDiameter={arcDiameter}
        arcHeight={ARC_HEIGHT}
        arcLeft={arcLeft}
        arcRadius={arcRadius}
        isExpanded={showTip}
      />
    </Animated.View>
  )
}
